import { CircleHelp, History, Map, Moon, Sun, User, Wallet, Zap } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, Navigate, NavLink, Outlet, useLocation } from 'react-router'
import { useActive } from '@/api/driver'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { useTheme } from '@/lib/theme'
import { cn } from '@/lib/utils'
import { WatchRunner } from '@/lib/watch'

const TABS = [
  { to: '/', label: 'Map', icon: Map, end: true },
  { to: '/charging', label: 'Charging', icon: Zap },
  { to: '/history', label: 'History', icon: History },
  { to: '/wallet', label: 'Wallet', icon: Wallet },
  { to: '/account', label: 'Account', icon: User },
]

export function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 font-semibold">
      <span className="grid size-8 place-items-center rounded-lg bg-ink text-brand">
        <Zap className="size-4 fill-current" />
      </span>
      <span>
        Saadhan <span className="text-brand-ink dark:text-brand">Charge</span>
      </span>
    </Link>
  )
}

function Toggles() {
  const { lang, setLang } = useI18n()
  const { dark, setTheme } = useTheme()
  return (
    <div className="flex items-center gap-1">
      <button className="h-8 rounded-lg px-2 text-[13px] font-medium text-text-2 hover:bg-surface-3" onClick={() => setLang(lang === 'en' ? 'ne' : 'en')}>
        {lang === 'en' ? 'नेपाली' : 'English'}
      </button>
      <button className="grid size-8 place-items-center rounded-lg text-text-2 hover:bg-surface-3" aria-label={dark ? 'Light mode' : 'Dark mode'} onClick={() => setTheme(dark ? 'light' : 'dark')}>
        {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </button>
    </div>
  )
}

export function Shell() {
  const { t } = useI18n()
  const { user } = useAuth()
  const active = useActive(!!user)
  const charging = (active.data?.active_sessions.length ?? 0) > 0
  const { pathname } = useLocation()
  const fullBleed = pathname === '/'

  const tab = (tb: (typeof TABS)[number], compact: boolean) => (
    <NavLink
      key={tb.to}
      to={tb.to}
      end={tb.end}
      className={({ isActive }) =>
        cn(
          compact ? 'flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px]' : 'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm',
          'relative font-medium transition-colors',
          isActive ? (compact ? 'text-text' : 'bg-surface-3 text-text') : 'text-text-2 hover:text-text',
        )
      }
    >
      <span className="relative">
        <tb.icon className={compact ? 'size-5' : 'size-4'} />
        {tb.to === '/charging' && charging && <span className="pulse-dot absolute -right-1 -top-1 size-2 rounded-full bg-brand" />}
      </span>
      {t(tb.label)}
    </NavLink>
  )

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-30 border-b border-border bg-surface/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-4 px-4">
          <Logo />
          <nav className="ml-4 hidden items-center gap-1 md:flex">{TABS.map((tb) => tab(tb, false))}</nav>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/help" aria-label={t('Help')} className="grid size-8 place-items-center rounded-lg text-text-2 hover:bg-surface-3"><CircleHelp className="size-4" /></Link>
            <Toggles />
            {!user && (
              <Link to="/login" className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-fg">
                {t('Sign in')}
              </Link>
            )}
          </div>
        </div>
      </header>
      <main className={cn('flex-1', fullBleed ? 'flex flex-col' : 'mx-auto w-full max-w-3xl px-4 py-5 pb-24 md:pb-10')}>
        <Outlet />
      </main>
      <WatchRunner />
      <nav className="pb-safe fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface md:hidden">{TABS.map((tb) => tab(tb, true))}</nav>
    </div>
  )
}

/** Pages that need an account send the driver to sign in and back. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const { pathname, search } = useLocation()
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(pathname + search)}`} replace />
  return <>{children}</>
}
